import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Eye, ShieldCheck } from 'lucide-react';
import { DataTable } from '../../../../components/ui/DataTable';
import { PageHeader } from '../../../../components/ui/PageHeader';
import { FilterBar } from '../../../../components/ui/FilterBar';
import { SearchInput } from '../../../../components/ui/SearchInput';
import { fetchStaffAdmins } from '../../../../features/admins/adminsThunks';
import {
  setSearchFilter,
  setPage,
  setRowsPerPage,
} from '../../../../features/admins/adminsSlice';

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '—');

/**
 * Every admin of the platform.
 *
 * The screen that only the internal team has: an admin manages its sellers, and
 * staff manages the admins — their plan, and which capabilities each one gets.
 */
export const Admins = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { admins, totalCount, loading, error, filters } = useSelector((state) => state.admins);

  const [searchInput, setSearchInput] = useState(filters.search);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchInput !== filters.search) {
        dispatch(setSearchFilter(searchInput));
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput, filters.search, dispatch]);

  useEffect(() => {
    dispatch(fetchStaffAdmins());
  }, [dispatch, filters.search, filters.page, filters.rowsPerPage]);

  const columns = [
    {
      header: 'ADMIN',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="font-bold text-ninja-dark dark:text-white">
            {`${row.user?.first_name || ''} ${row.user?.last_name || ''}`.trim() || 'Sin nombre'}
          </span>
          <span className="text-xs text-text-muted mt-0.5">{row.user?.email || '—'}</span>
        </div>
      ),
    },
    { header: 'RUT', cell: (row) => row.rut || '—' },
    {
      header: 'PLAN',
      cell: (row) => (
        <div className="flex flex-col items-start gap-1">
          <span className="px-2.5 py-1 rounded-lg bg-ninja-greenLight dark:bg-ninja-green/20 text-ninja-green text-xs font-bold">
            {row.plan_name || 'Sin plan'}
          </span>
          <span className="text-xs text-text-muted">{row.country_fk?.name || '—'}</span>
        </div>
      ),
    },
    {
      header: 'ACTIVIDAD',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="text-text-secondary">{formatDate(row.created_at)}</span>
          <span className="text-xs text-text-muted mt-0.5">
            Último acceso: {formatDate(row.user?.last_login)}
          </span>
        </div>
      ),
    },
    {
      header: 'ACCIONES',
      cell: (row) => (
        <div className="flex items-center justify-end">
          <button
            onClick={() => navigate(`/admins/${row.id}`)}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-ninja-green hover:bg-ninja-greenLight transition-colors"
            title="Ver detalle"
          >
            <Eye size={16} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div data-testid="admins-page" className="space-y-6 animate-in fade-in duration-300">
      <PageHeader
        icon={ShieldCheck}
        iconClassName="bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400"
        title="Operadores"
        description="Los operadores logísticos de la plataforma y el plan de cada uno."
      />

      <FilterBar variant="bare">
        <SearchInput
          variant="bare"
          value={searchInput}
          onChange={setSearchInput}
          placeholder="Buscar admins por nombre, rut o correo..."
        />
      </FilterBar>

      <DataTable
        columns={columns}
        data={admins}
        totalItems={totalCount}
        currentPage={filters.page}
        itemsPerPage={filters.rowsPerPage}
        isLoading={loading}
        error={error}
        onRetry={() => dispatch(fetchStaffAdmins())}
        itemLabel="admins"
        onPageChange={(page) => dispatch(setPage(page))}
        onRowsPerPageChange={(rowsPerPage) => dispatch(setRowsPerPage(rowsPerPage))}
      />
    </div>
  );
};
